import { ArrowRight, Settings, Users, TrendingUp, CheckCircle2, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import data from "../data/flexOfficeData.json";

const STEP_ICON_MAP = { Settings, Users, TrendingUp };

const useBreakpoint = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024 };
};

const FlexOfficeHowItWorks = () => {
  const nav = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const d = data.howItWorks;

  return (
    <section style={{ background: "#f8fafc", padding: isMobile ? "60px 20px" : isTablet ? "72px 32px" : "96px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 64 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <ChevronRight style={{ width: 14, height: 14, color: "#1d4ed8" }} />
            <span style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{d.badge}</span>
          </div>
          <h2 style={{ fontSize: isMobile ? 28 : isTablet ? 38 : 48, fontWeight: 900, letterSpacing: "-0.02em", color: "#0f172a", lineHeight: 1.1, margin: "0 0 16px" }}>
            {d.headline} <span style={{ color: "#1d4ed8" }}>{d.headlineAccent}</span>
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 18, color: "#64748b", maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>{d.description}</p>
        </div>

        {/* Steps */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 20 : 24, marginBottom: isMobile ? 32 : 48 }}>
          {d.steps.map((step, i) => {
            const IconComp = STEP_ICON_MAP[step.icon];
            const isLast = i === d.steps.length - 1;

            return (
              <div key={i} style={{ position: "relative" }}>
                {/* Connector arrow between steps (desktop only) */}
                {!isMobile && !isTablet && !isLast && (
                  <div style={{
                    position: "absolute",
                    top: 40,
                    right: -14,
                    zIndex: 10,
                    width: 28,
                    height: 28,
                    background: "white",
                    border: "2px solid #e2e8f0",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                  }}>
                    <ArrowRight style={{ width: 13, height: 13, color: "#94a3b8" }} />
                  </div>
                )}

                <div
                  style={{
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: 20,
                    padding: isMobile ? 24 : 32,
                    height: "100%",
                    boxSizing: "border-box",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = "#bfdbfe";
                    e.currentTarget.style.boxShadow = "0 12px 40px rgba(29,78,216,0.10)";
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "none";
                  }}
                >
                  {/* Step number watermark */}
                  <div style={{
                    position: "absolute",
                    top: 16,
                    right: 20,
                    fontSize: 64,
                    fontWeight: 900,
                    color: "#f1f5f9",
                    lineHeight: 1,
                    userSelect: "none",
                    pointerEvents: "none",
                    letterSpacing: "-0.04em"
                  }}>
                    {step.step}
                  </div>

                  {/* Icon */}
                  <div style={{
                    width: 52,
                    height: 52,
                    background: step.bg,
                    borderRadius: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 20,
                    color: step.color,
                    position: "relative",
                    zIndex: 1
                  }}>
                    {IconComp && <IconComp style={{ width: 24, height: 24 }} />}
                  </div>

                  {/* Step label */}
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: step.bg,
                    border: `1px solid ${step.color}22`,
                    padding: "4px 10px",
                    borderRadius: 100,
                    marginBottom: 12,
                    position: "relative",
                    zIndex: 1
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: step.color, letterSpacing: "0.05em" }}>
                      STEP {step.step}
                    </span>
                  </div>

                  <h3 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: "#0f172a", marginBottom: 10, lineHeight: 1.3, position: "relative", zIndex: 1 }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, marginBottom: 20, position: "relative", zIndex: 1 }}>
                    {step.description}
                  </p>

                  {/* Checkpoints */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, position: "relative", zIndex: 1 }}>
                    {step.checkpoints.map((point, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <CheckCircle2 style={{ width: 16, height: 16, color: step.color, flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{point}</span>
                      </div>
                    ))}
                  </div>

                  {/* Bottom accent bar */}
                  <div style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(to right, ${step.color}, #60a5fa)`,
                    borderBottomLeftRadius: 20,
                    borderBottomRightRadius: 20,
                  }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          borderRadius: 24,
          padding: isMobile ? "40px 24px" : isTablet ? "48px 36px" : "56px 48px",
          marginTop: isMobile ? 32 : 48,
          textAlign: "center",
          position: "relative",
          overflow: "hidden"
        }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)", backgroundSize: "40px 40px" }} />
          <div style={{ position: "relative" }}>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: isMobile ? 14 : 15, marginBottom: 12, fontWeight: 600, letterSpacing: "0.05em" }}>READY TO GET STARTED?</p>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: isMobile ? 14 : 16, maxWidth: 540, margin: "0 auto 32px", lineHeight: 1.7 }}>{d.footerText}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              {d.buttons.map((btn, i) => (
                <button
                  key={i}
                  onClick={() => nav("/contact")}
                  style={{
                    background: btn.type === "primary" ? "#1d4ed8" : "transparent",
                    color: "white",
                    border: btn.type === "secondary" ? "2px solid rgba(255,255,255,0.25)" : "none",
                    padding: isMobile ? "12px 28px" : "14px 36px",
                    borderRadius: 12,
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "all 0.25s"
                  }}
                  onMouseEnter={e => {
                    if (btn.type === "primary") { e.currentTarget.style.background = "#1e3a8a"; e.currentTarget.style.transform = "translateY(-1px)"; }
                    else { e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }
                  }}
                  onMouseLeave={e => {
                    if (btn.type === "primary") { e.currentTarget.style.background = "#1d4ed8"; e.currentTarget.style.transform = "none"; }
                    else { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.background = "transparent"; }
                  }}
                >
                  {btn.label}
                  {btn.type === "primary" && <ArrowRight style={{ width: 17, height: 17 }} />}
                </button>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FlexOfficeHowItWorks;